import { NavLink } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getNavRoutes, type AppRoute } from '@/config/routes';

/**
 * Sidebar component props
 */
export interface SidebarProps {
  /**
   * Navigation items to display; defaults to the routes the user may open
   */
  navItems?: readonly AppRoute[];

  /**
   * Optional title for the sidebar header
   */
  title?: string;

  /**
   * Optional subtitle for the sidebar header
   */
  subtitle?: string;

  /**
   * Optional callback when logout is clicked
   */
  onLogout?: () => void;

  /**
   * Optional additional CSS class
   */
  className?: string;
}

/**
 * Sidebar navigation component providing the main navigation for the application.
 * Navigation items come from the route config (config/routes.ts), filtered by
 * the user's permissions.
 *
 * @example
 * ```tsx
 * <Sidebar title="CDN Manager" subtitle="Admin Panel" onLogout={handleLogout} />
 * ```
 */
export function Sidebar({
  navItems,
  title = 'CDN Manager',
  subtitle = 'Admin Panel',
  onLogout,
  className = '',
}: SidebarProps) {
  const { user } = useAuth();
  const items = navItems ?? getNavRoutes(user?.role);

  const sidebarClasses = ['layout-sidebar', className].filter(Boolean).join(' ');

  return (
    <aside className={sidebarClasses}>
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <h1 className="sidebar-title">{title}</h1>
        <span className="sidebar-subtitle">{subtitle}</span>
      </div>

      {/* Navigation Links */}
      <nav className="sidebar-nav" aria-label="Main navigation">
        <ul className="nav-list">
          {items.map((item) => (
            <li key={item.path} className="nav-item">
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'nav-link-active' : ''}`
                }
                end={item.path === '/'}
              >
                <span className="nav-link-icon" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="nav-link-label">{item.navLabel}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Info & Logout */}
      <div className="sidebar-footer">
        <div className="user-info">
          <span className="user-email" title={user?.email}>
            {user?.email}
          </span>
          <span className="user-role">
            {user?.role && (
              <span className={`role-badge role-badge-${user.role}`}>
                {user.role}
              </span>
            )}
          </span>
        </div>
        {onLogout && (
          <button
            type="button"
            className="logout-button"
            onClick={onLogout}
            aria-label="Logout"
          >
            Logout
          </button>
        )}
      </div>
    </aside>
  );
}
